import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { palette_set_open } from '../../redux/actions';
import { pageHref } from '../../Helpers/pageHref';
import {
  useCommandPalette,
  buildStatusMessage,
  buildUnavailableNotice,
  isScopeFullyUnavailable,
  conceptsFromResolvedScope,
  describeRecentSearch,
  nextConceptSelection,
  shouldSearchOnConceptPick,
  entityTypesForConcepts,
  recentSearchesForDisplay,
  resultsMatchCurrentQuery,
  DEFAULT_TOP_N,
  MIN_QUERY_LENGTH,
  GlobalSearchHit,
  RecentSearch,
} from './useCommandPalette';
import { readRecentSearches, rememberRecentSearch, clearRecentSearches } from './recentSearchStorage';
import AuthService from '../../Services/authService';

// Global record search overlay (CD-5425). Opened with Ctrl/Cmd-K, or from the decoy control inside
// NavSearchFlyout.
//
// RECORDS ONLY - this panel deliberately does not search menu labels. NavSearchFlyout keeps that
// job, and the two stay separate because they have different interaction models: menu search is
// instant as-you-type, record search is type-then-submit. One panel with both would show some
// results immediately and others only on Enter, which is harder to explain than two panels with
// one behaviour each.
export default function CommandPalette() {
  const dispatch = useDispatch();
  const isOpen = useSelector((state: any) => state.palette?.open);
  const seedText = useSelector((state: any) => state.palette?.seedText);

  const {
    query,
    setQuery,
    submittedQuery,
    result,
    isSearching,
    errorMessage,
    validationMessage,
    search,
    reset,
    concepts,
    activeConcepts,
    setActiveConcepts,
  } = useCommandPalette();

  // The union of the picked chips' entity types, or null for "everything I may see". Null rather than an
  // empty array matters: the service reads null as "the caller did not scope", whereas an empty list would
  // be an explicit request for nothing. Either way a resolved "Concept:" prefix in the query overrides
  // this - the chips are then synced to what it resolved to, so they never end up describing a scope
  // other than the one that ran.
  const activeEntityTypes = entityTypesForConcepts(concepts, activeConcepts);

  // Plain click selects only that chip; ctrl/cmd-click adds or removes. See nextConceptSelection for the
  // rule and for why clicking the only-selected chip clears it.
  //
  // After a search, a plain click also re-runs it under the new scope, like switching a tab; a ctrl-click
  // still only builds the selection, which the user commits with Enter or the Search button. See
  // shouldSearchOnConceptPick for why.
  const pickConcept = (key: string, additive: boolean) => {
    const nextConcepts = nextConceptSelection(activeConcepts, key, additive);
    setActiveConcepts(nextConcepts);

    if (shouldSearchOnConceptPick(additive, submittedQuery)) {
      search(query, entityTypesForConcepts(concepts, nextConcepts));
    }
  };

  // What the next search will actually cover, said in words. This is what replaces the "Everything"
  // chip: with no chips lit the row alone is ambiguous - it could read as "nothing selected, nothing
  // will be searched" - so the scope is always stated explicitly.
  const scopeSummary = activeConcepts.length === 0 ? 'everything' : activeConcepts.join(', ');

  const [activeIndex, setActiveIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Recent searches are stored per user, not per browser profile - localStorage is shared by everyone
  // using the profile, and Bravo holds several accounts at once. Read on each use rather than captured
  // once, so switching the active user switches the list with it.
  const currentUserId = () => AuthService.getInstance().GetLoggedInUser()?.userId;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  // Whatever had focus when the palette opened, so closing can hand it back. Without this a keyboard
  // user who opens the palette and presses Esc is returned to the top of the document, losing their
  // place - the palette is reachable from anywhere, so "the top of the document" is rarely near it.
  const openerRef = useRef<HTMLElement | null>(null);

  // A flat view of every hit, in render order, so the arrow keys can walk across group boundaries
  // as one list - which is how a keyboard user perceives it.
  const flatHits = useMemo(() => {
    if (!result || !result.groups) return [] as GlobalSearchHit[];
    return result.groups.reduce((all: GlobalSearchHit[], group) => all.concat(group.items || []), []);
  }, [result]);

  // Types the caller asked for and the gate refused. Both the visible text and the spoken one are built
  // from these same two helpers, so the two accounts cannot drift apart.
  const unavailableNotice = buildUnavailableNotice(result?.unavailableGroups);
  const scopeFullyUnavailable = !!result && isScopeFullyUnavailable(result);

  // What the live region says. It deliberately mirrors what is already on screen rather than adding
  // anything of its own, so the spoken and visible accounts cannot drift apart.
  const statusMessage = useMemo(
    () =>
      buildStatusMessage({
        isSearching,
        errorMessage,
        validationMessage,
        submittedQuery,
        hitCount: flatHits.length,
        groups: result?.groups,
        unavailableGroups: result?.unavailableGroups,
        searchedTypeCount: result?.searchedTypeCount,
      }),
    [isSearching, errorMessage, validationMessage, submittedQuery, flatHits, result]
  );

  // Seed from a keystroke forwarded by the decoy control, and focus on open. Without the seed, the
  // one predictable failure is someone typing into what looks like a field and seeing nothing.
  //
  // useLayoutEffect, and the DOM value is written directly alongside the state, on purpose. The
  // decoy forwards the FIRST character and the user keeps typing immediately, so the remaining
  // keystrokes can reach the input before React has committed the seeded value - and each onChange
  // reports the DOM's own value, silently dropping the seed. (Typing "tucson" on the decoy produced
  // "ucson".) Writing the DOM value synchronously here closes that window; the caret is parked at
  // the end so the next character appends rather than overwriting.
  useLayoutEffect(() => {
    if (!isOpen) {
      reset();
      setActiveIndex(0);

      // Only if the opener is still in the document. Opening from the nav flyout's decoy closes that
      // flyout, so by now the button that was clicked is unmounted and focusing it does nothing at
      // all - focus silently lands on <body> and the user restarts from the top of the page. The rail
      // button is the decoy's own parent control and always mounted, so it is the honest fallback.
      const opener =
        openerRef.current && document.contains(openerRef.current)
          ? openerRef.current
          : document.querySelector<HTMLElement>('button.nav-ico[aria-label="Search menu"]');

      if (opener && typeof opener.focus === 'function') {
        opener.focus();
      }

      openerRef.current = null;

      return;
    }

    openerRef.current = document.activeElement as HTMLElement;
    setRecentSearches(readRecentSearches(currentUserId()));

    const seed = seedText || '';
    setQuery(seed);

    if (inputRef.current) {
      inputRef.current.value = seed;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(seed.length, seed.length);
    }
  }, [isOpen, seedText, reset, setQuery]);

  useEffect(() => {
    setActiveIndex(0);
  }, [result]);

  // Make the chips say what the search actually did.
  //
  // A "Client:" prefix beats the chips on the server, so without this the panel could contradict itself
  // in four places at once - the Evaluator chip lit, the summary reading "Searching Evaluator", the
  // scope echo saying Client, and Client being what actually ran. Folding the resolved scope back into
  // the chips leaves ONE piece of state, so there is nothing left to disagree.
  //
  // The prefix is consumed from the input at the same time, for the same reason: leaving it there would
  // re-apply it on the next search and silently override whatever the user did to the chips in between.
  useEffect(() => {
    if (!result) return;

    const synced = conceptsFromResolvedScope(
      result.resolvedConcepts,
      concepts.map((concept) => concept.key)
    );

    if (synced === null) return;

    setActiveConcepts(synced);

    if (result.effectiveQuery != null) {
      setQuery(result.effectiveQuery);
    }
  }, [result, concepts, setActiveConcepts, setQuery]);

  // Remember a search only once it has FOUND something, and count it.
  //
  // ⚠️ REPEATING THE SAME SEARCH INSIDE ONE SESSION DOES NOT INCREMENT. The second run is served from
  // the in-memory cache, so `result` keeps the same object identity and this effect does not re-fire.
  // Left as it is deliberately: it means a double-Enter or a re-run while refining counts once, and what
  // does count is coming back to the same search on another day - which is the better signal of what
  // someone actually relies on. Worth knowing before reading anything into the numbers.
  //
  // Recording every submit instead would fill the list with the typos and half-words the user corrected
  // a second later - those are exactly the searches that return nothing, so "found something" filters
  // them out without having to guess at intent. The scope is stored alongside the text because the same
  // words under a different scope are a different question, and re-running one as the other would
  // quietly answer the wrong one.
  useEffect(() => {
    if (!result || !submittedQuery) return;

    const hitCount = (result.groups || []).reduce((total, group) => total + (group.items || []).length, 0);

    if (hitCount === 0) return;

    // effectiveQuery when a prefix was consumed, so the remembered text matches what the chips now say
    // rather than repeating a prefix that is no longer in the box.
    setRecentSearches(
      rememberRecentSearch(currentUserId(), {
        query: result.effectiveQuery || submittedQuery,
        concepts:
          conceptsFromResolvedScope(
            result.resolvedConcepts,
            concepts.map((c) => c.key)
          ) || activeConcepts,
      })
    );

    // Deliberately keyed on `result` alone. The other values read here describe the search that produced
    // this result, so re-running when THEY change would re-remember the same search under a scope the
    // user has since altered - the list would fill with entries nobody ever ran.
  }, [result]);

  const runRecentSearch = (entry: RecentSearch) => {
    setQuery(entry.query);
    setActiveConcepts(entry.concepts);

    // The concepts are passed explicitly rather than read back from state: setActiveConcepts has not
    // committed yet at this point, so activeEntityTypes would still describe the previous selection.
    const entityTypes =
      entry.concepts.length === 0
        ? null
        : concepts
            .filter((concept) => entry.concepts.includes(concept.key))
            .reduce((all: string[], concept) => all.concat(concept.entityTypes || []), []);

    search(entry.query, entityTypes);
  };

  const close = () => dispatch(palette_set_open(false));

  // Ctrl/Cmd-click (and Ctrl/Cmd-Enter) opens the record in a new tab and LEAVES THE PALETTE OPEN, so a
  // run of results can be opened one after another without searching again. The rows are <button>s rather
  // than links, so the browser gives us none of this for free - without it, Ctrl-clicking a result
  // navigates the current tab like an ordinary click and silently loses the user's place.
  const goToHit = (hit: GlobalSearchHit, openInNewTab = false) => {
    if (!hit || !hit.detailUrl) return;

    const href = pageHref(hit.detailUrl);

    if (openInNewTab) {
      // noopener/noreferrer: the new tab must not get a handle on this window.
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    close();
    window.location.href = href;
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Keep Tab inside the panel. The overlay covers the app but does not make it inert, so without
    // this Tab walks into the page behind - 19 reachable controls on the Home page alone - and a
    // screen-reader user ends up operating a page they cannot see, with no way back to the palette.
    if (event.key === 'Tab') {
      const panel = panelRef.current;

      if (panel) {
        const focusable = Array.from(
          panel.querySelectorAll<HTMLElement>('button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])')
        ).filter((element) => element.offsetParent !== null);

        if (focusable.length > 0) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (flatHits.length === 0 ? 0 : (current + 1) % flatHits.length));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (flatHits.length === 0 ? 0 : (current - 1 + flatHits.length) % flatHits.length));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Enter picks the highlighted hit once results are on screen, and submits before that.
      if (flatHits.length > 0 && resultsMatchCurrentQuery(query, submittedQuery, result?.effectiveQuery)) {
        //The keyboard equivalent of Ctrl-clicking a row, so the two ways of picking a result behave alike.
        goToHit(flatHits[activeIndex], event.ctrlKey || event.metaKey);
      } else {
        search(query, activeEntityTypes);
      }
    }
  };

  if (!isOpen) return null;

  let flatIndex = -1;

  return (
    // The whole overlay lives inside .bravo-ds on purpose: the palette mounts at app.js level,
    // outside the sidebar's wrapper, and every design-system rule (including the [data-theme='dark']
    // overrides) is scoped to that class. Without it the panel renders with light chrome while the
    // theme's tokens have flipped to light text - which is exactly how the first version looked.
    <div className='bravo-ds'>
      <div
        className='flyout-catch cmdp-catch'
        onClick={close}
      />
      {/* role/aria-modal rather than a bare <aside>: an aside announces as "complementary", which tells a
          screen-reader user nothing has changed. As a labelled modal dialog the panel is announced by
          name on open, and the label points at the same heading sighted users read. */}
      <aside
        ref={panelRef}
        className='flyout cmdp-panel'
        role='dialog'
        aria-modal='true'
        aria-labelledby='cmdp-title'
        onKeyDown={onKeyDown}
      >
        {/* Same head idiom as NavSearchFlyout, so the two search panels read as siblings. */}
        <div className='flyout-head'>
          <div className='fh-txt'>
            <div className='eyebrow'>Search</div>
            <h3 id='cmdp-title'>Find a record</h3>
          </div>
          <div className='sp' />
          <button
            type='button'
            className='fclose'
            onClick={close}
            aria-label='Close'
          >
            <i className='fa-solid fa-xmark' />
          </button>
        </div>

        <div className='flyout-search cmdp-search'>
          <i className='fa-solid fa-magnifying-glass' />
          <input
            ref={inputRef}
            type='text'
            /* Deliberately NO LONGER a list of concepts. It used to name them all, so that nobody read
               "invoices" here and got nothing back - but that was written when there were no chips. With
               seven concepts the list neither fits nor stays true, and the chip row directly below is now
               the authoritative, role-filtered answer to "what can I search?". The user guide still
               enumerates them, because it has room. */
            placeholder='Search records by name or id&hellip;'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label='Search records'
            /* The arrow keys move a highlight while focus stays in this box, so the highlight only
               exists visually unless it is published here. aria-activedescendant is what makes the
               arrow keys audible: it names the option the user is currently on. */
            role='combobox'
            aria-expanded={flatHits.length > 0}
            aria-controls='cmdp-results'
            aria-activedescendant={flatHits.length > 0 ? `cmdp-option-${activeIndex}` : undefined}
          />
          <button
            type='button'
            className='cmdp-go'
            onClick={() => search(query, activeEntityTypes)}
            disabled={isSearching}
          >
            Search
          </button>
        </div>

        {concepts.length > 1 ? (
          <div className='cmdp-chips'>
            {concepts.map((concept) => {
              const picked = activeConcepts.includes(concept.key);
              return (
                <button
                  type='button'
                  key={concept.key}
                  className={`chip cmdp-chip${picked ? ' active' : ''}`}
                  aria-pressed={picked}
                  onClick={(event) => pickConcept(concept.key, event.ctrlKey || event.metaKey)}
                  /* The keyboard path needs its own handler, and stopPropagation is the point of it.
                     The panel's onKeyDown claims Enter for "submit the search", which ran BEFORE a
                     focused chip could activate - so tabbing to a chip and pressing Enter searched
                     instead of selecting it, and the chips were unusable without a mouse. Ctrl-Enter did
                     nothing at all, because a browser fires no click for it. */
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;

                    event.preventDefault();
                    event.stopPropagation();
                    pickConcept(concept.key, event.ctrlKey || event.metaKey);
                  }}
                  title={`Search ${concept.key} only. Ctrl-click to add or remove without clearing the rest.`}
                >
                  {concept.key}
                </button>
              );
            })}
            <span className='cmdp-chips-note'>Searching {scopeSummary}</span>
          </div>
        ) : null}

        {/* The resolved-scope echo used to be rendered here, as a second row of chips plus the effective
            query. It is gone on purpose: now that a resolved prefix syncs into the real chip row, the echo
            would say the same thing twice - and when it disagreed, it was the half that was wrong. The
            widen affordance it carried lives on in the empty-results hint, which is where the user needs
            it. */}
        {/* Results came back, but part of the requested scope was refused. Said above the list rather
            than inside it, so it is not scrolled away with the rows it qualifies. */}
        {unavailableNotice && !scopeFullyUnavailable && flatHits.length > 0 ? (
          <div className='cmdp-unavailable'>
            <i className='fa-solid fa-circle-info' /> {unavailableNotice}
          </div>
        ) : null}

        <div className='flyout-list'>
          {isSearching ? (
            <div className='flyout-hint'>
              <i className='fa-solid fa-circle-notch fa-spin' /> Searching&hellip;
            </div>
          ) : errorMessage ? (
            <div className='flyout-hint cmdp-error'>{errorMessage}</div>
          ) : validationMessage ? (
            <div className='flyout-hint'>{validationMessage}</div>
          ) : !submittedQuery ? (
            <>
              <div className='flyout-hint'>
                Type a name or id and press <kbd>Enter</kbd> to search <b>{scopeSummary}</b>&hellip;
              </div>

              {/* Offered only before a search, where the panel is otherwise empty - it costs the user
                  nothing to ignore and saves retyping a query they have already got right once. */}
              {recentSearches.length > 0 ? (
                <>
                  <div
                    className='cmdp-group-head'
                    aria-hidden='true'
                  >
                    <span className='cmdp-group-label'>Recent searches</span>
                    <button
                      type='button'
                      className='cmdp-scope-clear'
                      onClick={() => setRecentSearches(clearRecentSearches(currentUserId()))}
                    >
                      Clear
                    </button>
                  </div>

                  {recentSearchesForDisplay(recentSearches).map((entry) => (
                    <button
                      type='button'
                      key={describeRecentSearch(entry)}
                      className='flyout-item'
                      onClick={() => runRecentSearch(entry)}
                    >
                      <span className='sr-txt'>
                        <span className='sr-page'>{entry.query}</span>
                        {entry.concepts.length > 0 ? (
                          <span className='sr-section'>{entry.concepts.join(', ')}</span>
                        ) : (
                          <span className='sr-section'>everything</span>
                        )}
                      </span>
                    </button>
                  ))}
                </>
              ) : null}
            </>
          ) : scopeFullyUnavailable ? (
            /* Nothing was queried at all, so the ordinary "no records match" would be a claim about data
               the search never read - and the user would conclude the record does not exist rather than
               that they cannot see it. Same reasoning as the widen affordance just below. */
            <div className='flyout-hint'>{unavailableNotice}</div>
          ) : flatHits.length === 0 ? (
            <div className='flyout-hint'>
              No records match &ldquo;{submittedQuery}&rdquo;
              {activeConcepts.length > 0 ? <> in {scopeSummary}</> : null}.
              {unavailableNotice ? <> {unavailableNotice}</> : null}
              {/* Without this, starting scoped would silently hide records and a user would conclude the
                  record does not exist. The widen action is offered exactly where that conclusion forms. */}
              {activeConcepts.length > 0 ? (
                <>
                  {' '}
                  <button
                    type='button'
                    className='cmdp-scope-clear'
                    onClick={() => {
                      setActiveConcepts([]);
                      search(query, null);
                    }}
                  >
                    Search everything instead
                  </button>
                </>
              ) : null}
            </div>
          ) : (
            /* A listbox may contain only options, or groups of them - so each entity type becomes a
               role='group' carrying its label, and the visible heading is hidden from the tree rather
               than left to announce a second time. */
            <div
              id='cmdp-results'
              role='listbox'
              aria-label='Search results'
            >
              {(result?.groups || []).map((group) => (
                <div
                  key={group.entityType}
                  role='group'
                  aria-label={group.label}
                >
                  <div
                    className='cmdp-group-head'
                    aria-hidden='true'
                  >
                    <span className='cmdp-group-label'>{group.label}</span>
                    {/* The server sends no true total, so at the cap we say "8+" and invite a
                        refinement rather than implying an exact count. */}
                    <span className='cmdp-group-count'>
                      {group.count >= DEFAULT_TOP_N ? `${DEFAULT_TOP_N}+ · refine` : group.count}
                    </span>
                  </div>

                  {(group.items || []).map((hit) => {
                    flatIndex += 1;
                    const isActive = flatIndex === activeIndex;
                    return (
                      <button
                        type='button'
                        key={`${group.entityType}-${hit.id}`}
                        id={`cmdp-option-${flatIndex}`}
                        role='option'
                        aria-selected={isActive}
                        className={`flyout-item${isActive ? ' active' : ''}`}
                        onClick={(event) => goToHit(hit, event.ctrlKey || event.metaKey)}
                        /* Same reason as the chips: the panel's Enter shortcut opens whichever row the
                           ARROW KEYS highlighted, which is not necessarily the row Tab has focused. A
                           focused row must open itself. */
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;

                          event.preventDefault();
                          event.stopPropagation();
                          goToHit(hit, event.ctrlKey || event.metaKey);
                        }}
                        onMouseEnter={(
                          (index) => () =>
                            setActiveIndex(index)
                        )(flatIndex)}
                      >
                        {/* Reuses the flyout's own two-line row shape (.sr-txt / .sr-section /
                            .sr-page) so a result here looks like a result there. */}
                        <span className='sr-txt'>
                          <span className='sr-page'>{hit.title}</span>
                          {hit.subTitle ? <span className='sr-section'>{hit.subTitle}</span> : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* The outcome, announced. Results replace the panel's contents silently otherwise: a
            screen-reader user presses Enter and hears nothing at all, with no way to tell a slow
            search from an empty one. Kept off-screen because the same facts are already on screen. */}
        <div
          className='cmdp-sr-only'
          role='status'
          aria-live='polite'
        >
          {statusMessage}
        </div>

        <div className='cmdp-foot'>
          <span>
            <kbd>&uarr;</kbd> <kbd>&darr;</kbd> move
          </span>
          <span>
            <kbd>Enter</kbd> open
          </span>
          <span>
            <kbd>Ctrl</kbd> click new tab
          </span>
          <span>
            <kbd>Ctrl</kbd> chip multi-select
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
          {/* The "Scope with Client:" hint used to sit here. Removed deliberately: it advertised a second
              way to do exactly what the chips above already do, and advertising it was how the two ended up
              set to different things. The prefix still works for anyone who knows it - it now lights the
              matching chip - but the chips are what the panel teaches. */}
          <span className='cmdp-foot-note'>
            <kbd>Ctrl</kbd> <kbd>K</kbd> from anywhere
          </span>
        </div>
      </aside>
    </div>
  );
}
